import React, { useEffect, useState } from "react";
import { Query, Column } from "material-table";
import { useSnackbar } from "notistack";
import { Invoice, getInvoices } from "api/owner/reports";
import moment from "moment";
import { DefaultTable } from "components";
import { useMounted } from "utils/hooks";
import i18n from "./i18n";
import ExportInvoicesDialog from "./ExportInvoicesDialog";
import "./documentsStyle.scss";

// prop types
interface InvoicesTabProps {
    clubId?: string,
    onSetClubId: (clubId?: string) => void,
}

// currency formatter
const formatter = Intl.NumberFormat("de-DE", { style: "currency", currency: "EUR" });

// owner accounting table for invoices
const Invoices = ({ clubId, onSetClubId }: InvoicesTabProps) => {
    // snackbar
    const { enqueueSnackbar } = useSnackbar();
    const displayError = (error: any) => enqueueSnackbar(
        `${i18n.anErrorOccurred}: ${error.description} (${error.type})`,
        { variant: "error" },
    );

    // ui state
    const [dateInInterval, setDateInInterval] = useState<string | null>(null);
    const [showExportDialog, setShowExportDialog] = useState(false);

    // convert material-table query to API params
    const convertQueryToParams = (query: Query<Invoice>) => {
        // paging
        const params: Parameters<typeof getInvoices>[0] = {
            limit: query.pageSize,
            offset: query.pageSize * query.page,
        };

        // sorting
        if (query.orderBy) {
            params.sortOrder = query.orderDirection.toUpperCase() as typeof params["sortOrder"];
            params.sortKey = query.orderBy.field as typeof params["sortKey"];
        }

        // filter
        params.clubId = clubId;
        if (dateInInterval) {
            params.dateInInterval = dateInInterval;
            params.reportingInterval = "month";
        }

        return params;
    };

    // table ref for refresh
    const tableRef = React.useRef<any>(null);
    const isMounted = useMounted();
    const refreshTable = () => {
        if (!isMounted) return; // skip on initial material-table fetch
        if (tableRef.current) tableRef.current.onQueryChange();
    };
    useEffect(refreshTable, [clubId, dateInInterval]);

    // table actions
    const tableActions = [
        ({ invoices }: Invoice) => ({
            icon: "get_app",
            tooltip: i18n.download,
            onClick: () => { invoices.forEach(({ pdfUrl }) => window.open(pdfUrl)); },
        }),
        {
            icon: "save_alt",
            isFreeAction: true,
            tooltip: i18n.export,
            onClick: () => setShowExportDialog(true),
        },
    ];

    // table columns
    const tableColumns: Column<Invoice>[] = [
        {
            field: "user",
            render: ({ user }) => `${user.firstName} ${user.lastName}`,
            sorting: false,
            title: i18n.recipient,
        },
        {
            field: "booking.clubName",
            sorting: false,
            title: i18n.club,
        },
        {
            field: "startDate",
            render: ({ booking }) => moment(booking.startDate).format("DD.MM.YYYY HH:mm"),
            title: i18n.bookingOf,
        },
        {
            field: "payingPriceCents",
            render: ({ payingPriceCents }) => formatter.format(payingPriceCents / 100),
            sorting: false,
            title: i18n.amount,
        },
    ];

    // render
    return (
        <>
            {/* invoices table */}
            <DefaultTable
                actions={tableActions}
                columns={tableColumns}
                data={(query) => new Promise((resolve) => {
                    const params = convertQueryToParams(query);
                    getInvoices(params)
                        .then((response) => resolve({
                            ...response,
                            page: query.page,
                        }))
                        .catch((err) => {
                            displayError(err);
                            resolve({ data: [], page: 0, totalCount: 0 });
                        });
                })}
                options={{ search: false }}
                tableRef={tableRef}
                title={i18n.invoices}
            />

            {/* export dialog */}
            <ExportInvoicesDialog
                clubId={clubId}
                dateInInterval={dateInInterval}
                showDialog={showExportDialog}
                onClose={() => setShowExportDialog(false)}
                onSetClubId={onSetClubId}
                onSetDateInInterval={setDateInInterval}
            />
        </>
    );
};

export default Invoices;
